import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { DemoOrganization } from '@/types/demo.types';
import { persistStore } from 'src/persist-store';
import { useDemoOrgData } from '@/hooks/organization/useDemoOrgData';
import { PERSONAL_ORG_ID } from '@/mockData/common/demoFilters';
import { useOrg } from './OrgContext';

interface DemoOrgContextType {
  selectedOrg: DemoOrganization | null;
  isPersonalMode: boolean;
  setSelectedOrg: (org: DemoOrganization | null) => void;
  setPersonalMode: () => void;
  getCurrentProfile: () => {
    displayName: string;
    email: string;
    accountType: string;
    profileImage?: string;
  };
  getNavbarProfile: () => {
    displayName: string;
    email: string;
    accountType: string;
    profileImage?: string;
  };
}

const DemoOrgContext = createContext<DemoOrgContextType | undefined>(undefined);

const DEMO_STORAGE_KEY = 'demo-storage';

interface DemoStorageData {
  selectedOrgId: string | null;
  mode: 'org' | 'personal';
}

interface DemoOrgProviderProps {
  children: ReactNode;
}

export const DemoOrgProvider: React.FC<DemoOrgProviderProps> = ({ children }) => {
  const { organizations: demoOrganizations, userProfile: demoUserProfile } = useDemoOrgData();
  const { setSelectedOrgId } = useOrg();
  const [selectedOrg, setSelectedOrgState] = useState<DemoOrganization | null>(null);
  const [isPersonalMode, setIsPersonalModeState] = useState<boolean>(true);

  const syncOrgContext = (orgId: string | null) => {
    void setSelectedOrgId(orgId ?? PERSONAL_ORG_ID);
  };

  // Load persisted state on mount
  useEffect(() => {
    const loadPersistedState = async () => {
      try {
        const demoStorage = await persistStore.getSession<DemoStorageData>(DEMO_STORAGE_KEY);

        if (demoStorage?.mode === 'personal') {
          setIsPersonalModeState(true);
          setSelectedOrgState(null);
          syncOrgContext(null);
          window.dispatchEvent(new CustomEvent('demo-org-change', { detail: { orgId: null } }));
        } else if (demoStorage?.selectedOrgId) {
          const org = demoOrganizations.find(o => o.orgId === demoStorage.selectedOrgId);
          if (org) {
            setSelectedOrgState(org);
            setIsPersonalModeState(false);
          }
          syncOrgContext(demoStorage.selectedOrgId);
          window.dispatchEvent(new CustomEvent('demo-org-change', { detail: { orgId: demoStorage.selectedOrgId } }));
        } else {
          syncOrgContext(null);
          window.dispatchEvent(new CustomEvent('demo-org-change', { detail: { orgId: null } }));
        }
      } catch (error) {
        console.error('Error loading demo storage:', error);
      }
    };

    loadPersistedState();
  }, [demoOrganizations]);

  const setSelectedOrg = async (org: DemoOrganization | null) => {
    setSelectedOrgState(org);
    setIsPersonalModeState(false);
    syncOrgContext(org?.orgId ?? null);
    window.dispatchEvent(new CustomEvent('demo-org-change', { detail: { orgId: org?.orgId ?? null } }));

    try {
      if (org) {
        await persistStore.putSession<DemoStorageData>(DEMO_STORAGE_KEY, {
          selectedOrgId: org.orgId,
          mode: 'org'
        });
      } else {
        await persistStore.putSession<DemoStorageData>(DEMO_STORAGE_KEY, {
          selectedOrgId: null,
          mode: 'personal'
        });
        setIsPersonalModeState(true);
      }
    } catch (error) {
      console.error('Error saving demo storage:', error);
    }
  };

  const setPersonalMode = async () => {
    setSelectedOrgState(null);
    setIsPersonalModeState(true);
    syncOrgContext(null);
    window.dispatchEvent(new CustomEvent('demo-org-change', { detail: { orgId: null } }));

    try {
      await persistStore.putSession<DemoStorageData>(DEMO_STORAGE_KEY, {
        selectedOrgId: null,
        mode: 'personal'
      });
    } catch (error) {
      console.error('Error saving demo storage:', error);
    }
  };

  const getCurrentProfile = () => {
    if (!isPersonalMode && selectedOrg?.demoUser) {
      return selectedOrg.demoUser;
    }

    return demoUserProfile;
  };

  const getNavbarProfile = () => {
    if (isPersonalMode || !selectedOrg) {
      return demoUserProfile;
    }
    
    // Return organization profile for navbar avatar only
    return {
      displayName: selectedOrg.name,
      email: selectedOrg.billingEmail || 'contact@organization.com',
      accountType: selectedOrg.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      profileImage: selectedOrg.logoUrl
    };
  };

  const value: DemoOrgContextType = {
    selectedOrg,
    isPersonalMode,
    setSelectedOrg,
    setPersonalMode,
    getCurrentProfile,
    getNavbarProfile
  };

  return (
    <DemoOrgContext.Provider value={value}>
      {children}
    </DemoOrgContext.Provider>
  );
};

export const useDemoOrg = (): DemoOrgContextType | null => {
  const context = useContext(DemoOrgContext);
  if (!context) {
    return null;
  }
  return context;
};
